import { useMemo } from 'react';
import { useRefacturationCalculations } from '../pl/useRefacturationCalculations';
import { useQuotePartCalculations } from '../pl/useQuotePartCalculations';
import { useConsultantCalculations } from '../pl/useConsultantCalculations';
import { round2 } from '../pl/useQuarterHelpers';

/**
 * Hook qui calcule le CA net total après quote-part (identique à la section P&L Refacturation).
 * Formule par consultant : Refacturation HT - Quote-part = CA net
 * Retourne la somme de tous les consultants.
 *
 * @param {Array} tempsData - Données de temps (déjà filtrées par année)
 * @param {Array} charges - Charges filtrées par année (avec consultantNom enrichi)
 * @param {Object} consultantConfig - Config consultants par année (objet ou nombre)
 * @param {number} filterAnnee - Année sélectionnée
 * @returns {{ caTotal: { Q1, Q2, Q3, Q4, ANNUEL } }}
 */
export function useRefacturationTotal(tempsData, charges, consultantConfig, filterAnnee) {
  // 1. Refacturation par consultant (jours normaux × TMP recalculé + CA tmpexcl)
  const { refacturationByConsultant: rawRefacturation, consultants: tempsConsultants } = useRefacturationCalculations(tempsData);

  // 1b. Fusionner les consultants config + temps
  const allConsultants = useMemo(() => {
    const entry = consultantConfig[filterAnnee];
    const configNames = (entry && typeof entry === 'object' && entry.consultantNames)
      ? entry.consultantNames
      : [];
    const set = new Set([...tempsConsultants, ...configNames]);
    return [...set];
  }, [tempsConsultants, consultantConfig, filterAnnee]);

  // 1c. Enrichir la refacturation avec des entrées zéro pour les consultants config-only
  const refacturationByConsultant = useMemo(() => {
    const result = { ...rawRefacturation };
    allConsultants.forEach(name => {
      if (!result[name]) {
        result[name] = { Q1: 0, Q2: 0, Q3: 0, Q4: 0, ANNUEL: 0 };
      }
    });
    return result;
  }, [rawRefacturation, allConsultants]);

  // 2. Quote-part par consultant (charges partagées / nb consultants + charges spécifiques)
  const quotePart = useQuotePartCalculations(charges, consultantConfig, filterAnnee, allConsultants);

  // 3. CA net par consultant = refacturation - quote-part
  const caNet = useConsultantCalculations(refacturationByConsultant, quotePart);

  // 4. Somme de tous les consultants
  const caTotal = useMemo(() => {
    const result = { Q1: 0, Q2: 0, Q3: 0, Q4: 0, ANNUEL: 0 };

    Object.values(caNet).forEach(data => {
      result.Q1 += data.Q1 || 0;
      result.Q2 += data.Q2 || 0;
      result.Q3 += data.Q3 || 0;
      result.Q4 += data.Q4 || 0;
      result.ANNUEL += data.ANNUEL || 0;
    });

    result.Q1 = round2(result.Q1);
    result.Q2 = round2(result.Q2);
    result.Q3 = round2(result.Q3);
    result.Q4 = round2(result.Q4);
    result.ANNUEL = round2(result.ANNUEL);

    return result;
  }, [caNet]);

  return { caTotal };
}
